import { ArrowRight, CheckCircle2, LockKeyhole, ScanSearch } from 'lucide-react';
import { SEO_PAGES, type SeoPageContent } from './seo-pages.js';

export function SeoPage({ page }: { page: SeoPageContent }) {
  const relatedPages = Object.values(SEO_PAGES).filter((candidate) => candidate.key !== page.key);

  return (
    <div className="guide-shell">
      <header className="guide-header">
        <a className="guide-brand" href="/" aria-label="ORIGNTD home">
          <img src="/brand/origntd-lockup-horizontal.png" alt="ORIGNTD" />
        </a>
        <nav aria-label="Guide navigation">
          <a href="/#verification-workspace">Free checker</a>
          <a href="/what-is-c2pa/">C2PA</a>
          <a href="/is-this-image-ai/">AI image question</a>
          <a href="/image-metadata-viewer/">Metadata</a>
        </nav>
      </header>

      <main>
        <section className="guide-hero">
          <div className="guide-hero__copy">
            <span className="guide-eyebrow">ORIGNTD / {page.eyebrow}</span>
            <h1>{page.title}</h1>
            <p className="guide-answer">{page.answer}</p>
            <p className="guide-introduction">{page.introduction}</p>
            <div className="guide-actions">
              <a className="guide-primary" href="/#verification-workspace">Check an image free <ArrowRight size={17} /></a>
              <span><LockKeyhole size={15} /> Local-first by default</span>
            </div>
          </div>
          <aside className="guide-proof" aria-label={page.proofLabel}>
            <div className="guide-proof__header"><ScanSearch size={18} /><span>{page.proofLabel}</span></div>
            {page.proofItems.map((item) => (
              <div className="guide-proof__item" key={item.label}>
                <CheckCircle2 size={16} aria-hidden="true" />
                <span><strong>{item.label}</strong>{item.value}</span>
              </div>
            ))}
          </aside>
        </section>

        <section className="guide-boundary" aria-label="Evidence boundary">
          <strong>Evidence boundary</strong>
          <span>Provenance does not establish factual truth. Missing provenance does not establish human authorship.</span>
        </section>

        <article className="guide-article">
          {page.sections.map((section, index) => (
            <section key={section.title}>
              <span className="guide-section-number">{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h2>{section.title}</h2>
                {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets && (
                  <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>
                )}
              </div>
            </section>
          ))}
        </article>

        <section className="guide-faq" aria-labelledby="guide-faq-title">
          <div>
            <span className="guide-eyebrow">Clear answers</span>
            <h2 id="guide-faq-title">Frequently asked questions</h2>
          </div>
          <div className="guide-faq__items">
            {page.faqs.map((faq) => (
              <article key={faq.question}>
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="guide-cta">
          <span className="guide-eyebrow">See the evidence</span>
          <h2>Inspect the file you actually have.</h2>
          <p>Run the free local-first check, then read the evidence and its limits instead of relying on a guessed verdict.</p>
          <a className="guide-primary" href="/#verification-workspace">Open the free checker <ArrowRight size={17} /></a>
        </section>

        <aside className="guide-references" aria-labelledby="guide-references-title">
          <strong id="guide-references-title">Primary references</strong>
          {page.references.map((reference) => (
            <a href={reference.url} rel="noreferrer" target="_blank" key={reference.url}>{reference.label} <span aria-hidden="true">↗</span></a>
          ))}
        </aside>

        <nav className="guide-related" aria-label="Related guides">
          <strong>Continue exploring</strong>
          {relatedPages.map((related) => <a href={`/${related.key}/`} key={related.key}>{related.title}</a>)}
        </nav>
      </main>

      <footer className="guide-footer">
        <span><strong>ORIGNTD</strong> · Evidence before verdict</span>
        <p>Free preliminary technical inspection. Not legal advice, rights clearance or a guarantee of authenticity.</p>
      </footer>
    </div>
  );
}
